import React, { useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Bold, Italic, Heading2, Heading3, List, ListOrdered, Link as LinkIcon, Image as ImageIcon, Quote, Eye, Pencil } from 'lucide-react';

/**
 * Hybrid markdown editor: toolbar wraps the selected text (or inserts a
 * placeholder when nothing is selected) with the corresponding markdown
 * syntax. A live preview tab renders the same markdown using react-markdown
 * + remark-gfm so the author sees exactly what visitors will get.
 *
 * Why this design:
 * - The underlying storage stays as plain markdown — any existing blog post
 *   that contains only paragraphs keeps working.
 * - No WYSIWYG HTML soup, no XSS risk (ReactMarkdown strips raw HTML by default).
 * - Tiny dependency footprint (react-markdown + remark-gfm ≈ 50 KB gzipped).
 */
const MarkdownEditor = ({ value, onChange, placeholder, minHeight = '320px', testId = 'markdown-editor' }) => {
  const textareaRef = useRef(null);
  const [mode, setMode] = useState('write'); // 'write' | 'preview'

  /**
   * Wrap the current selection with `before`/`after`, or insert `placeholder`
   * when nothing is selected. Restores caret/selection inside the wrapped
   * text so the user can immediately keep typing.
   */
  const surround = (before, after = before, placeholder = 'text') => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const selected = value.slice(start, end);
    const body = selected || placeholder;
    const next = value.slice(0, start) + before + body + after + value.slice(end);
    onChange(next);
    // Restore selection after React rerenders
    requestAnimationFrame(() => {
      ta.focus();
      const cursorStart = start + before.length;
      ta.setSelectionRange(cursorStart, cursorStart + body.length);
    });
  };

  /** Insert markdown at the start of each selected line (for headings / lists). */
  const prefixLines = (prefix) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    // Find the start of the first selected line
    const lineStart = value.lastIndexOf('\n', start - 1) + 1;
    const block = value.slice(lineStart, end);
    const lines = block.split('\n');
    const transformed = lines.map(l => (l.startsWith(prefix) ? l : prefix + l)).join('\n');
    const next = value.slice(0, lineStart) + transformed + value.slice(end);
    onChange(next);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(lineStart, lineStart + transformed.length);
    });
  };

  const insertLink = () => {
    const url = window.prompt('URL', 'https://');
    if (!url) return;
    surround('[', `](${url})`, 'tekst');
  };

  const insertImage = () => {
    const url = window.prompt('Afbeelding URL', 'https://');
    if (!url) return;
    const alt = window.prompt('Alt-tekst (voor SEO)', '') || 'afbeelding';
    const ta = textareaRef.current;
    const start = ta ? ta.selectionStart : value.length;
    const snippet = `\n![${alt}](${url})\n`;
    const next = value.slice(0, start) + snippet + value.slice(start);
    onChange(next);
  };

  const tools = [
    { icon: Heading2, title: 'Kop 2', action: () => prefixLines('## ') },
    { icon: Heading3, title: 'Kop 3', action: () => prefixLines('### ') },
    { icon: Bold, title: 'Vet', action: () => surround('**', '**', 'vette tekst') },
    { icon: Italic, title: 'Cursief', action: () => surround('*', '*', 'cursief') },
    { icon: List, title: 'Opsomming', action: () => prefixLines('- ') },
    { icon: ListOrdered, title: 'Genummerde lijst', action: () => prefixLines('1. ') },
    { icon: Quote, title: 'Citaat', action: () => prefixLines('> ') },
    { icon: LinkIcon, title: 'Link', action: insertLink },
    { icon: ImageIcon, title: 'Afbeelding', action: insertImage },
  ];

  return (
    <div style={styles.wrapper} data-testid={testId}>
      <div style={styles.toolbar}>
        <div style={styles.tools}>
          {tools.map(({ icon: Icon, title, action }) => (
            <button
              key={title}
              type="button"
              onClick={action}
              style={styles.toolBtn}
              title={title}
              aria-label={title}
              data-testid={`md-tool-${title.toLowerCase().replace(/\s/g, '-')}`}
            >
              <Icon size={15} />
            </button>
          ))}
        </div>
        <div style={styles.tabs}>
          <button
            type="button"
            onClick={() => setMode('write')}
            style={mode === 'write' ? styles.tabActive : styles.tab}
            data-testid="md-tab-write"
          >
            <Pencil size={13} /> Schrijven
          </button>
          <button
            type="button"
            onClick={() => setMode('preview')}
            style={mode === 'preview' ? styles.tabActive : styles.tab}
            data-testid="md-tab-preview"
          >
            <Eye size={13} /> Voorbeeld
          </button>
        </div>
      </div>

      {mode === 'write' ? (
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || 'Schrijf hier je artikel in Markdown...\n\n## Een tussenkop\n\nGewone alinea met **vet** en *cursief*.\n\n- Bullet 1\n- Bullet 2'}
          style={{ ...styles.textarea, minHeight }}
        />
      ) : (
        <div style={{ ...styles.preview, minHeight }} data-testid="md-preview">
          {value.trim() ? (
            <MarkdownRenderer content={value} />
          ) : (
            <p style={styles.previewEmpty}>Nog niets om te tonen — schrijf eerst content.</p>
          )}
        </div>
      )}

      <p style={styles.hint}>
        Markdown ondersteund: <code>## Kop</code> · <code>**vet**</code> · <code>*cursief*</code> · <code>- bullet</code> · <code>[link](url)</code> · <code>![alt](afbeelding-url)</code>
      </p>
    </div>
  );
};

/**
 * Slugify any React children (string or nested elements) into a stable
 * lowercase URL fragment. Used to generate `id` attributes on h2/h3 so the
 * blog table-of-contents can scroll-link to them.
 */
const slugifyChildren = (children) => {
  let text = '';
  const walk = (node) => {
    if (node == null) return;
    if (typeof node === 'string' || typeof node === 'number') { text += node; return; }
    if (Array.isArray(node)) { node.forEach(walk); return; }
    if (node.props && node.props.children) walk(node.props.children);
  };
  walk(children);
  return text
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
};

/**
 * Pure renderer used by both the editor preview and the public blog page.
 * Reuses one component so styling stays consistent.
 *
 * h2/h3 receive auto-generated `id` attributes derived from their text so the
 * Table of Contents in BlogDetail can `scrollIntoView` them. Down-side is
 * that renaming a heading invalidates any incoming anchor links — acceptable
 * trade-off since blogs rarely link to their own sub-headings externally.
 */
/**
 * Heading renderers — kept at module scope to avoid recreating component
 * types on every render (React would otherwise tear down and rebuild the
 * heading DOM, which also breaks the IntersectionObserver in BlogDetail).
 */
const H1 = ({ node, children, ...props }) => <h2 id={slugifyChildren(children)} style={rendered.h2} {...props}>{children}</h2>;
const H2 = ({ node, children, ...props }) => <h2 id={slugifyChildren(children)} style={rendered.h2} {...props}>{children}</h2>;
const H3 = ({ node, children, ...props }) => <h3 id={slugifyChildren(children)} style={rendered.h3} {...props}>{children}</h3>;
const P = ({ node, ...props }) => <p style={rendered.p} {...props} />;
const A = ({ node, ...props }) => <a style={rendered.a} target="_blank" rel="noopener noreferrer" {...props} />;
const Ul = ({ node, ...props }) => <ul style={rendered.ul} {...props} />;
const Ol = ({ node, ...props }) => <ol style={rendered.ol} {...props} />;
const Li = ({ node, ...props }) => <li style={rendered.li} {...props} />;
const Blockquote = ({ node, ...props }) => <blockquote style={rendered.blockquote} {...props} />;
const Strong = ({ node, ...props }) => <strong style={rendered.strong} {...props} />;
const Em = ({ node, ...props }) => <em style={rendered.em} {...props} />;
const Img = ({ node, ...props }) => <img style={rendered.img} alt={props.alt || ''} {...props} />;
const Hr = () => <hr style={rendered.hr} />;
const CodeR = ({ inline, ...props }) =>
  inline ? <code style={rendered.codeInline} {...props} /> : <pre style={rendered.codeBlock}><code {...props} /></pre>;

const MD_COMPONENTS = {
  h1: H1, h2: H2, h3: H3,
  p: P, a: A, ul: Ul, ol: Ol, li: Li,
  blockquote: Blockquote, strong: Strong, em: Em, img: Img, hr: Hr, code: CodeR,
};

export const MarkdownRenderer = ({ content }) => (
  <div className="md-rendered">
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
      {content}
    </ReactMarkdown>
  </div>
);

/**
 * Parses a raw markdown string and returns the H2/H3 outline that the blog
 * detail page uses for its table of contents. The slug logic MUST match
 * `slugifyChildren` so the anchor links land on the right element.
 */
export const extractHeadings = (markdown = '') => {
  const lines = markdown.split('\n');
  const out = [];
  // Skip headings that live inside ``` fenced code blocks.
  let inCode = false;
  for (const line of lines) {
    if (line.startsWith('```')) { inCode = !inCode; continue; }
    if (inCode) continue;
    const m = /^(#{1,3})\s+(.+?)\s*$/.exec(line);
    if (!m) continue;
    const level = m[1].length === 1 ? 2 : m[1].length; // treat h1 same as h2 (matches renderer)
    const text = m[2].replace(/[*_`]/g, '').trim();
    const id = text
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80);
    out.push({ level, text, id });
  }
  return out;
};

const styles = {
  wrapper: {
    backgroundColor: 'white',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#EAE5DD',
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: '0.5rem',
    padding: '0.5rem 0.75rem',
    backgroundColor: '#FAF9F6',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: '#EAE5DD',
  },
  tools: { display: 'flex', flexWrap: 'wrap', gap: '0.25rem' },
  toolBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    backgroundColor: 'white',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#EAE5DD',
    color: '#585755',
    cursor: 'pointer',
    padding: 0,
  },
  tabs: { display: 'inline-flex', gap: '0.25rem' },
  tab: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.3rem',
    backgroundColor: 'transparent',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#EAE5DD',
    color: '#8C8B88',
    padding: '0.35rem 0.7rem',
    fontSize: '0.78rem',
    fontWeight: 500,
    cursor: 'pointer',
  },
  tabActive: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.3rem',
    backgroundColor: '#1A1918',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: '#1A1918',
    color: 'white',
    padding: '0.35rem 0.7rem',
    fontSize: '0.78rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  textarea: {
    width: '100%',
    padding: '1rem 1.1rem',
    border: 'none',
    backgroundColor: 'white',
    color: '#1A1918',
    fontFamily: "'JetBrains Mono', 'Courier New', monospace",
    fontSize: '0.9rem',
    lineHeight: 1.65,
    outline: 'none',
    resize: 'vertical',
    boxSizing: 'border-box',
  },
  preview: {
    padding: '1.5rem 1.75rem',
    backgroundColor: 'white',
    color: '#1A1918',
    overflowY: 'auto',
  },
  previewEmpty: { color: '#8C8B88', fontStyle: 'italic', margin: 0 },
  hint: {
    margin: 0,
    padding: '0.5rem 0.85rem',
    fontSize: '0.72rem',
    color: '#8C8B88',
    backgroundColor: '#FAF9F6',
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: '#EAE5DD',
  },
};

const rendered = {
  h2: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '1.6rem',
    color: '#1A1918',
    margin: '1.75rem 0 0.75rem',
    lineHeight: 1.3,
  },
  h3: {
    fontFamily: "'Playfair Display', serif",
    fontSize: '1.25rem',
    color: '#1A1918',
    margin: '1.5rem 0 0.6rem',
    lineHeight: 1.35,
  },
  p: { color: '#585755', fontSize: '1.05rem', lineHeight: 1.8, margin: '0 0 1rem' },
  a: { color: '#C5A059', textDecoration: 'underline' },
  ul: { paddingLeft: '1.5rem', margin: '0 0 1rem', color: '#585755' },
  ol: { paddingLeft: '1.5rem', margin: '0 0 1rem', color: '#585755' },
  li: { lineHeight: 1.8, margin: '0.25rem 0' },
  blockquote: {
    borderLeftWidth: '3px',
    borderLeftStyle: 'solid',
    borderLeftColor: '#C5A059',
    padding: '0.25rem 0 0.25rem 1rem',
    margin: '0 0 1rem',
    color: '#585755',
    fontStyle: 'italic',
  },
  strong: { color: '#1A1918', fontWeight: 700 },
  em: { fontStyle: 'italic' },
  img: { maxWidth: '100%', height: 'auto', margin: '1rem 0' },
  hr: { borderTop: '1px solid #EAE5DD', border: 'none', borderTopWidth: '1px', borderTopStyle: 'solid', borderTopColor: '#EAE5DD', margin: '2rem 0' },
  codeInline: {
    backgroundColor: '#F5F1E8',
    color: '#1A1918',
    padding: '0.1rem 0.35rem',
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '0.92em',
  },
  codeBlock: {
    backgroundColor: '#F5F1E8',
    padding: '1rem',
    overflowX: 'auto',
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '0.88rem',
    margin: '1rem 0',
  },
};

export default MarkdownEditor;
